import {
  AllowedArrayInnerTypes,
  DossDataFormat,
  FormFieldDefaultValue,
  TableCellData,
  dayJsToMidnightUTCDateTimeString,
  isArrayCellData,
  isBooleanCellData,
  isBooleanDefaultValue,
  isDateCellData,
  isDateDefaultValue,
  isDateTimeCellData,
  isDateTimeDefaultValue,
  isNumberCellData,
  isNumberDefaultValue,
  isStringCellData,
  isStringDefaultValue,
} from '@doss/common-all'
import { DataType } from '@doss/prisma/types'
import { LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import { DatePicker } from '~/@/components/inputs/date/date-picker/date-picker'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { Checkbox } from '~/@/components/inputs/standalone/checkbox'
import { LongTextInput } from '~/@/components/inputs/text/long-text-input'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Text } from '../ui/atoms/text/Text'

export enum DefaultValueContext {
  TABLE_COLUMN = 'TABLE_COLUMN',
  FORM_FIELD = 'FORM_FIELD',
}

// Define the type that filters out types that don't have `data`
type WithData<T> = T extends { data: unknown } ? T : never

// Apply this filter to TableCellData
type TableCellDataWithData = WithData<TableCellData>

type DefaultValueType<T extends DefaultValueContext> = T extends DefaultValueContext.TABLE_COLUMN
  ? TableCellData
  : FormFieldDefaultValue

interface DefaultValueFieldProps<T extends DefaultValueContext> {
  tableColumnType: DataType
  tableColumnInnerType: AllowedArrayInnerTypes | null
  defaultValue: DefaultValueType<T> | null
  setDefaultValue: (defaultValue: DefaultValueType<T> | null) => void
  enumOptions: string[]
  defaultValueIsRequired: boolean
  context: T
  label?: string
}

/**
 * Renders the field for the default value for a table column based on the type and inner type
 * of the table column or for a form field based on the form field type
 */
export function DefaultValueField<T extends DefaultValueContext>({
  defaultValue,
  setDefaultValue,
  tableColumnType,
  tableColumnInnerType,
  enumOptions,
  defaultValueIsRequired,
  context,
  label,
}: DefaultValueFieldProps<T>) {
  const { shortformLocale } = useGlobalContext()
  const defaultValueIsNotProvided =
    !defaultValue || (isArrayCellData(defaultValue) && isEmpty(defaultValue.data))
  const showError = defaultValueIsRequired && defaultValueIsNotProvided
  const { t } = useTypedTranslation()

  const helperText = t('shared.default_value_is_required')

  // Helper function to convert defaultValue based on the Dropdown type
  function getDropdownValue<T extends DefaultValueContext>(
    defaultValue: DefaultValueType<T> | null,
    isMultiple: true,
  ): string[]
  function getDropdownValue<T extends DefaultValueContext>(
    defaultValue: DefaultValueType<T> | null,
    isMultiple: false,
  ): string
  function getDropdownValue<T extends DefaultValueContext>(
    defaultValue: DefaultValueType<T> | null,
    isMultiple: boolean,
  ): string[] | string {
    if (!defaultValue) {
      return isMultiple ? [] : ''
    }

    // Handle TableCellData (which has 'type' property)
    if ('type' in defaultValue) {
      if (isMultiple) {
        if (defaultValue.type === DossDataFormat.ARRAY) {
          return defaultValue.data.map((value) => value.toString())
        }
        if (defaultValue.type === DossDataFormat.ENUM) {
          return [defaultValue.data]
        }
        return []
      } else {
        if (defaultValue.type === DossDataFormat.ARRAY && defaultValue.data.length) {
          return defaultValue.data[0].toString()
        }
        if (defaultValue.type === DossDataFormat.ENUM) {
          return defaultValue.data
        }
        return ''
      }
    }
    // Handle FormFieldDefaultValue (which only has 'data' property)
    else {
      if (isMultiple) {
        if (Array.isArray(defaultValue.data)) {
          return defaultValue.data.map((value) => value.toString())
        }
        return [defaultValue.data.toString()]
      } else {
        if (Array.isArray(defaultValue.data) && defaultValue.data.length) {
          return defaultValue.data[0].toString()
        }
        return defaultValue.data.toString()
      }
    }
  }

  // Function to create setDefaultValue
  function createSetDefaultValue<T extends DefaultValueContext>(
    setDefaultValue: (defaultValue: DefaultValueType<T> | null) => void,
    context: T,
  ) {
    return function setDefaultValueWithType<K extends DossDataFormat>(
      value: T extends DefaultValueContext.TABLE_COLUMN
        ? Extract<TableCellDataWithData, { type: K }>['data'] | null
        : FormFieldDefaultValue['data'] | null,
      type: K,
    ) {
      if (value === null) {
        setDefaultValue(null)
      } else if (context === DefaultValueContext.TABLE_COLUMN) {
        setDefaultValue({ type, data: value } as DefaultValueType<T>)
      } else {
        setDefaultValue({ data: value } as DefaultValueType<T>)
      }
    }
  }

  function createSetDefaultValueWrapper<T extends DefaultValueContext>(
    setDefaultValue: (defaultValue: DefaultValueType<T> | null) => void,
    context: T,
  ) {
    if (context === DefaultValueContext.TABLE_COLUMN) {
      return createSetDefaultValue(
        setDefaultValue as (defaultValue: TableCellData | null) => void,
        DefaultValueContext.TABLE_COLUMN,
      )
    } else {
      return createSetDefaultValue(
        setDefaultValue as (defaultValue: FormFieldDefaultValue | null) => void,
        DefaultValueContext.FORM_FIELD,
      )
    }
  }

  const setDefaultValueWithContext = createSetDefaultValueWrapper(setDefaultValue, context)

  switch (tableColumnType) {
    case DataType.NUMBER: {
      let numberValue: number | undefined
      if (context === DefaultValueContext.TABLE_COLUMN) {
        if (defaultValue && isNumberCellData(defaultValue)) {
          numberValue = defaultValue.data
        }
      } else {
        if (defaultValue && isNumberDefaultValue(defaultValue)) {
          numberValue = defaultValue.data
        }
      }
      return (
        <ShortTextInput
          errorText={(showError && helperText) || undefined}
          label={label}
          onChange={(event) => {
            const value = event.target.value
            if (value === '') {
              setDefaultValueWithContext(null, DossDataFormat.NUMBER)
            } else {
              setDefaultValueWithContext(Number(value), DossDataFormat.NUMBER)
            }
          }}
          type="number"
          value={numberValue}
        />
      )
    }
    case DataType.STRING: {
      let stringValue: string | undefined
      if (context === DefaultValueContext.TABLE_COLUMN) {
        if (defaultValue && isStringCellData(defaultValue)) {
          stringValue = defaultValue.data
        }
      } else {
        if (defaultValue && isStringDefaultValue(defaultValue)) {
          stringValue = defaultValue.data
        }
      }
      return (
        <LongTextInput
          errorText={(showError && helperText) || undefined}
          label={label}
          onChange={(event) => {
            const value = event.target.value
            if (value === '') {
              setDefaultValueWithContext(null, DossDataFormat.STRING)
            } else {
              setDefaultValueWithContext(event.target.value, DossDataFormat.STRING)
            }
          }}
          value={stringValue}
        />
      )
    }

    case DataType.BOOLEAN: {
      let booleanValue: boolean | undefined
      if (context === DefaultValueContext.TABLE_COLUMN) {
        if (defaultValue && isBooleanCellData(defaultValue)) {
          booleanValue = defaultValue.data
        }
      } else {
        if (defaultValue && isBooleanDefaultValue(defaultValue)) {
          booleanValue = defaultValue.data
        }
      }
      return (
        <Checkbox
          checked={booleanValue}
          label={label}
          onCheckedChange={(checked) => setDefaultValueWithContext(checked, DossDataFormat.BOOLEAN)}
        />
      )
    }

    case DataType.DATE: {
      let dateString: string | undefined
      if (context === DefaultValueContext.TABLE_COLUMN) {
        if (defaultValue && isDateCellData(defaultValue)) {
          dateString = defaultValue.data
        }
      } else {
        if (defaultValue && isDateDefaultValue(defaultValue)) {
          dateString = defaultValue.data
        }
      }

      let date: dayjs.Dayjs | 'now' | undefined
      if (dateString === 'now') {
        date = 'now'
      } else if (dateString) {
        date = dayjs.utc(dateString)
      } else {
        date = undefined
      }

      return (
        <>
          <LocalizationProvider adapterLocale={shortformLocale} dateAdapter={AdapterDayjs}>
            <DatePicker
              errorText={(showError && helperText) || undefined}
              label={label}
              onChange={(newDate) => {
                if (newDate === 'now') {
                  setDefaultValueWithContext('now', DossDataFormat.DATE)
                  return
                }
                // For DATE types, set to midnight UTC
                if (dayjs.isDayjs(newDate)) {
                  setDefaultValueWithContext(
                    newDate.isValid() ? dayJsToMidnightUTCDateTimeString(newDate) : '',
                    DossDataFormat.DATE,
                  )
                } else {
                  setDefaultValueWithContext(null, DossDataFormat.DATE)
                }
              }}
              showTodayButton
              value={date}
            />
          </LocalizationProvider>
        </>
      )
    }

    case DataType.DATE_TIME: {
      let dateTimeString: string | undefined
      if (context === DefaultValueContext.TABLE_COLUMN) {
        if (defaultValue && isDateTimeCellData(defaultValue)) {
          dateTimeString = defaultValue.data
        }
      } else {
        if (defaultValue && isDateTimeDefaultValue(defaultValue)) {
          dateTimeString = defaultValue.data
        }
      }

      let date: dayjs.Dayjs | 'now' | undefined
      if (dateTimeString === 'now') {
        date = 'now'
      } else if (dateTimeString) {
        date = dayjs(dateTimeString)
      } else {
        date = undefined
      }
      return (
        <>
          <LocalizationProvider adapterLocale={shortformLocale} dateAdapter={AdapterDayjs}>
            <DatePicker
              errorText={(showError && helperText) || undefined}
              label={label}
              onChange={(newDate) => {
                if (newDate === 'now') {
                  setDefaultValueWithContext('now', DossDataFormat.DATE)
                  return
                }
                if (dayjs.isDayjs(newDate)) {
                  setDefaultValueWithContext(
                    newDate.isValid() ? newDate.toISOString() : '',
                    DossDataFormat.DATE_TIME,
                  )
                } else {
                  setDefaultValueWithContext(null, DossDataFormat.DATE_TIME)
                }
              }}
              showTodayButton
              value={date}
              withTime
            />
          </LocalizationProvider>
        </>
      )
    }

    case DataType.ENUM: {
      if (isEmpty(enumOptions)) {
        return <Text className="p-1 text-gray-9">{t('shared.no_dropdown_options')}</Text>
      }

      const enumDefaultValue = getDropdownValue<T>(defaultValue, false)

      return (
        <Dropdown
          errorText={showError ? helperText : undefined}
          label={label}
          onChange={(newValue) => {
            if (newValue) {
              setDefaultValueWithContext(newValue, DossDataFormat.ENUM)
            } else {
              setDefaultValueWithContext(null, DossDataFormat.ENUM)
            }
          }}
          options={enumOptions.map((enumOption) => ({
            label: enumOption,
            value: enumOption,
          }))}
          showClearButton
          value={enumDefaultValue}
        />
      )
    }
    case DataType.ARRAY: {
      if (tableColumnInnerType === DataType.ENUM) {
        if (isEmpty(enumOptions)) {
          return <Text className="p-1 text-gray-9">{t('shared.no_dropdown_options')}</Text>
        }

        const enumDefaultValues = getDropdownValue<T>(defaultValue, true)
        return (
          <Dropdown
            errorText={showError ? helperText : undefined}
            isMultiSelect
            label={label}
            onChange={(selectedValues) => {
              setDefaultValueWithContext(selectedValues || [], DossDataFormat.ARRAY)
            }}
            options={enumOptions.map((enumOption) => ({
              label: enumOption,
              value: enumOption,
            }))}
            values={enumDefaultValues}
          />
        )
      }
      return null
    }
    // Do not handle default values for the following data types
    case DataType.FILE_URL:
    case DataType.USER:
    case DataType.ID:
    case DataType.LOOKUP:
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return null
  }
}
